import { parse } from '@customerio/legally-safe-just-bash';
import type {
  ScriptNode,
  StatementNode,
  PipelineNode,
  CommandNode,
  SimpleCommandNode,
  WordNode,
} from '@customerio/legally-safe-just-bash';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type CurlResource = {
  /** HTTP method (GET, POST, PUT, PATCH, DELETE, etc.) */
  method: string;
  /** The full URL */
  url: string;
  /** The resource path extracted from the URL (e.g. /v1/campaigns/123) */
  path: string;
  /** The resource type extracted from the path (e.g. "campaigns") */
  resourceType: string;
  /** Whether this is a mutating request (POST, PUT, PATCH, DELETE) */
  isMutating: boolean;
};

export type AnalysisResult = {
  /**
   * `true` when every command in the script was fully analyzed — all commands
   * are either curl calls with statically-resolvable URLs or known-safe
   * read-only helpers (jq, grep, head, …).
   *
   * When `false`, the script contains at least one command we couldn't fully
   * verify, so the caller should **not** auto-approve based on resources alone.
   */
  fullyAnalyzed: boolean;
  /** All curl resources found in the command. */
  resources: CurlResource[];
  /**
   * Human-readable reasons the analysis is incomplete (`fullyAnalyzed=false`).
   * Empty when `fullyAnalyzed` is `true`.
   */
  warnings: string[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Commands that are read-only / pure-formatting helpers.
 * They never mutate remote resources or the local system in a meaningful way.
 */
const SAFE_READONLY_COMMANDS = new Set([
  // JSON / text processing
  'jq',
  'yq',
  'grep',
  'egrep',
  'fgrep',
  // Note: sed and awk are intentionally excluded — `sed -i` writes files and
  // awk can redirect internally. They trigger fullyAnalyzed=false, which now
  // requires manual approval.
  'cut',
  'tr',
  'sort',
  'uniq',
  'wc',
  'head',
  'tail',

  'cat',
  'less',
  'more',
  'column',
  'fold',
  'fmt',
  'paste',
  'join',
  'comm',
  'diff',
  'colordiff',
  // Output
  'echo',
  'printf',
  'true',
  'false',
  // Encoding / hashing (read-only transforms)
  'base64',
  'md5sum',
  'sha256sum',
  'sha1sum',
  'xxd',
  'od',
  'hexdump',
  // Variable / test helpers that don't have side effects
  'test',
  '[',
  '[[',
  // date for timestamp generation
  'date',
]);

/**
 * curl options that consume the next argument as a value.
 */
const CURL_OPTIONS_WITH_VALUE = new Set([
  '-X',
  '--request',
  '-H',
  '--header',
  '-d',
  '--data',
  '--data-raw',
  '--data-binary',
  '--data-urlencode',
  '--data-ascii',
  '-F',
  '--form',
  '--form-string',
  '-o',
  '--output',
  '-u',
  '--user',
  '-A',
  '--user-agent',
  '-e',
  '--referer',
  '-b',
  '--cookie',
  '-c',
  '--cookie-jar',
  // -T / --upload-file handled separately (implies PUT)
  '-w',
  '--write-out',
  '-m',
  '--max-time',
  '--connect-timeout',
  '-r',
  '--range',
  '-x',
  '--proxy',
  '-U',
  '--proxy-user',
  '--resolve',
  '--retry',
  '--retry-delay',
  '--retry-max-time',
  '-E',
  '--cert',
  '--key',
  '--cacert',
  '--capath',
  '-K',
  '--config',
  '--interface',
  '--local-port',
  '--max-redirs',
  '--limit-rate',
  '--proto',
  '--proto-redir',
  '--dns-servers',
  '--trace',
  '--trace-ascii',
  '--stderr',
  '-D',
  '--dump-header',
  '--max-filesize',
  '--url',
]);

// ---------------------------------------------------------------------------
// Word resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a WordNode to a static string value.
 * Returns `null` if the word contains dynamic content that can't be resolved
 * at parse time (variable expansion, command substitution, …).
 *
 * `nestedScripts` collects any nested ScriptNodes found in CommandSubstitution
 * or ProcessSubstitution parts so the caller can analyze them too.
 */
function resolveWord(word: WordNode, nestedScripts?: ScriptNode[]): string | null {
  let result = '';
  for (const part of word.parts) {
    switch (part.type) {
      case 'Literal':
        result += part.value;
        break;
      case 'SingleQuoted':
        result += part.value;
        break;
      case 'Escaped':
        result += part.value;
        break;
      case 'DoubleQuoted': {
        let hasUnresolvable = false;
        for (const inner of part.parts) {
          if (inner.type === 'Literal') {
            result += inner.value;
          } else if (inner.type === 'Escaped') {
            result += inner.value;
          } else if (inner.type === 'CommandSubstitution') {
            // Can't resolve the value, but collect ALL nested scripts before returning
            nestedScripts?.push(inner.body);
            hasUnresolvable = true;
          } else {
            // ParameterExpansion, ArithmeticExpansion, etc.
            hasUnresolvable = true;
          }
        }
        if (hasUnresolvable) return null;
        break;
      }
      case 'CommandSubstitution':
        nestedScripts?.push(part.body);
        return null;
      case 'ProcessSubstitution':
        nestedScripts?.push(part.body);
        return null;
      default:
        // ParameterExpansion, ArithmeticExpansion, BraceExpansion, Glob, Tilde
        return null;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Curl argument parsing
// ---------------------------------------------------------------------------

function parseCurlArgs(cmd: SimpleCommandNode, nestedScripts: ScriptNode[]): CurlResource | null {
  const args = cmd.args.map((a) => resolveWord(a, nestedScripts));

  let method: string | null = null;
  let url: string | null = null;
  let hasData = false;

  let i = 0;
  while (i < args.length) {
    let arg = args[i];

    if (arg == null) {
      i++;
      continue;
    }

    // Normalize --option=value → split into --option + value so all
    // downstream checks work uniformly (e.g. --request=DELETE, --data=payload).
    let eqValue: string | null = null;
    if (arg.startsWith('--') && arg.includes('=')) {
      const eqIdx = arg.indexOf('=');
      eqValue = arg.slice(eqIdx + 1);
      arg = arg.slice(0, eqIdx);
    }

    if (arg === '-X' || arg === '--request') {
      const val = eqValue ?? args[i + 1];
      if (val != null) {
        method = val.toUpperCase();
      } else {
        // Method is dynamic / unresolvable — can't determine safety
        return null;
      }
      i += eqValue != null ? 1 : 2;
      continue;
    }

    // Handle -XMETHOD (short flag with value concatenated, no space)
    if (arg.startsWith('-X') && arg.length > 2) {
      method = arg.slice(2).toUpperCase();
      i++;
      continue;
    }

    if (arg === '--url') {
      const val = eqValue ?? args[i + 1];
      if (val != null) {
        url = val;
      }
      i += eqValue != null ? 1 : 2;
      continue;
    }

    if (
      arg === '-d' ||
      arg === '--data' ||
      arg === '--data-raw' ||
      arg === '--data-binary' ||
      arg === '--data-urlencode' ||
      arg === '--data-ascii' ||
      arg === '-F' ||
      arg === '--form' ||
      arg === '--form-string'
    ) {
      hasData = true;
      i += eqValue != null ? 1 : 2;
      continue;
    }

    // -T / --upload-file implies PUT
    if (arg === '-T' || arg === '--upload-file') {
      method ??= 'PUT';
      i += eqValue != null ? 1 : 2;
      continue;
    }

    if (CURL_OPTIONS_WITH_VALUE.has(arg)) {
      i += eqValue != null ? 1 : 2;
      continue;
    }

    if (arg.startsWith('-')) {
      i++;
      continue;
    }

    // Positional arg = URL
    url ??= arg;

    i++;
  }

  if (url == null) {
    return null;
  }

  method ??= hasData ? 'POST' : 'GET';

  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    const m = url.match(/(?:https?:\/\/[^/]+)?(\/[^\s#?]*)/);
    path = m?.[1] ?? url;
  }

  return {
    method,
    url,
    path,
    resourceType: extractResourceType(path),
    isMutating: MUTATING_METHODS.has(method),
  };
}

// ---------------------------------------------------------------------------
// Resource type extraction
// ---------------------------------------------------------------------------

function extractResourceType(path: string): string {
  const segments = path.split('/').filter(Boolean);
  const meaningful = segments.filter((s) => !/^(v\d+|api)$/i.test(s));
  const resourceParts: string[] = [];
  for (const segment of meaningful) {
    if (!isIdentifier(segment)) {
      resourceParts.push(segment);
    }
  }
  const joined = resourceParts.join('/');
  return joined !== '' ? joined : path;
}

function isIdentifier(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true;
  if (/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(segment)) return true;
  if (/^[\da-f]{8,}$/i.test(segment)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// AST walking — exhaustive verification
// ---------------------------------------------------------------------------

type WalkContext = {
  resources: CurlResource[];
  warnings: string[];
  fullyAnalyzed: boolean;
};

function warn(ctx: WalkContext, message: string) {
  ctx.warnings.push(message);
  ctx.fullyAnalyzed = false;
}

function walkScript(script: ScriptNode, ctx: WalkContext) {
  for (const stmt of script.statements) {
    walkStatement(stmt, ctx);
  }
}

function walkStatement(stmt: StatementNode, ctx: WalkContext) {
  if (stmt.background) {
    warn(ctx, 'Background execution (&) detected');
  }
  for (const pipeline of stmt.pipelines) {
    walkPipeline(pipeline, ctx);
  }
}

function walkPipeline(pipeline: PipelineNode, ctx: WalkContext) {
  for (const cmd of pipeline.commands) {
    walkCommand(cmd, ctx);
  }
}

function walkCommand(cmd: CommandNode, ctx: WalkContext) {
  switch (cmd.type) {
    case 'SimpleCommand':
      walkSimpleCommand(cmd, ctx);
      break;
    case 'If':
      warn(ctx, 'Conditional (if) detected — cannot statically verify all branches');
      for (const clause of cmd.clauses) {
        for (const s of clause.condition) walkStatement(s, ctx);
        for (const s of clause.body) walkStatement(s, ctx);
      }
      if (cmd.elseBody != null) {
        for (const s of cmd.elseBody) walkStatement(s, ctx);
      }
      break;
    case 'For':
    case 'CStyleFor':
    case 'While':
    case 'Until':
      warn(ctx, `Loop (${cmd.type.toLowerCase()}) detected — cannot statically verify iterations`);
      for (const s of cmd.body) walkStatement(s, ctx);
      break;
    case 'Case':
      warn(ctx, 'Case statement detected — cannot statically verify all branches');
      for (const item of cmd.items) {
        for (const s of item.body) walkStatement(s, ctx);
      }
      break;
    case 'Subshell':
      // Subshells are fine to walk into — same environment, just isolated
      for (const s of cmd.body) walkStatement(s, ctx);
      break;
    case 'Group':
      for (const s of cmd.body) walkStatement(s, ctx);
      break;
    case 'FunctionDef':
      warn(ctx, `Function definition (${cmd.name}) detected`);
      walkCommand(cmd.body, ctx);
      break;
    default:
      // ArithmeticCommand, ConditionalCommand — not expected to contain curl
      warn(ctx, `Unhandled compound command type: ${(cmd as CommandNode).type}`);
      break;
  }
}

function walkSimpleCommand(cmd: SimpleCommandNode, ctx: WalkContext) {
  // Assignment-only commands (VAR=value with no command name) — safe
  if (cmd.name == null) {
    if (cmd.assignments.length > 0) {
      // Walk into any nested scripts in assignment values
      for (const assignment of cmd.assignments) {
        if (assignment.value != null) {
          walkWordForNested(assignment.value, ctx);
        }
        if (assignment.array != null) {
          for (const word of assignment.array) {
            walkWordForNested(word, ctx);
          }
        }
      }
    }
    return;
  }

  const nestedScripts: ScriptNode[] = [];
  const name = resolveWord(cmd.name, nestedScripts);

  // Walk any nested scripts discovered in the command name itself
  for (const nested of nestedScripts) {
    walkScript(nested, ctx);
  }

  if (name == null) {
    warn(ctx, 'Command name contains dynamic content — cannot verify');
    // Still walk args for nested scripts
    walkArgsForNested(cmd, ctx);
    return;
  }

  if (name === 'curl') {
    const argNestedScripts: ScriptNode[] = [];
    const resource = parseCurlArgs(cmd, argNestedScripts);

    // Walk any nested scripts found in curl args (e.g. -d "$(curl ...)")
    for (const nested of argNestedScripts) {
      walkScript(nested, ctx);
    }

    if (resource != null) {
      ctx.resources.push(resource);
    } else {
      warn(ctx, 'curl command with unresolvable URL');
    }
    return;
  }

  if (SAFE_READONLY_COMMANDS.has(name)) {
    // Still walk args for any nested command/process substitutions
    walkArgsForNested(cmd, ctx);
    return;
  }

  // Unknown command — flag it
  warn(ctx, `Unknown command: ${name}`);
  walkArgsForNested(cmd, ctx);
}

/**
 * Walk all args of a SimpleCommand looking for nested scripts
 * (CommandSubstitution, ProcessSubstitution) inside word parts.
 */
function walkArgsForNested(cmd: SimpleCommandNode, ctx: WalkContext) {
  for (const arg of cmd.args) {
    walkWordForNested(arg, ctx);
  }
}

/**
 * Walk a single WordNode looking for nested scripts.
 */
function walkWordForNested(word: WordNode, ctx: WalkContext) {
  for (const part of word.parts) {
    if (part.type === 'CommandSubstitution') {
      walkScript(part.body, ctx);
    } else if (part.type === 'ProcessSubstitution') {
      walkScript(part.body, ctx);
    } else if (part.type === 'DoubleQuoted') {
      for (const inner of part.parts) {
        if (inner.type === 'CommandSubstitution') {
          walkScript(inner.body, ctx);
        } else if (inner.type === 'ProcessSubstitution') {
          walkScript(inner.body, ctx);
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Redirections check
// ---------------------------------------------------------------------------

/**
 * Check if any redirections in the script write to files.
 * Output redirections (>, >>, etc.) are side-effectful.
 */
function checkRedirections(script: ScriptNode, ctx: WalkContext) {
  function visitCmd(cmd: CommandNode) {
    if (cmd.type === 'SimpleCommand' && cmd.redirections.length > 0) {
      for (const redir of cmd.redirections) {
        if (
          redir.operator === '>' ||
          redir.operator === '>>' ||
          redir.operator === '>|' ||
          redir.operator === '&>' ||
          redir.operator === '&>>'
        ) {
          warn(ctx, `Output redirection (${redir.operator}) detected — writes to file`);
        }
      }
    }
    if (cmd.type === 'If') {
      for (const clause of cmd.clauses) {
        for (const s of clause.condition) visitStmt(s);
        for (const s of clause.body) visitStmt(s);
      }
      if (cmd.elseBody != null) for (const s of cmd.elseBody) visitStmt(s);
    } else if (cmd.type === 'For' || cmd.type === 'CStyleFor' || cmd.type === 'While' || cmd.type === 'Until') {
      for (const s of cmd.body) visitStmt(s);
    } else if (cmd.type === 'Case') {
      for (const item of cmd.items) for (const s of item.body) visitStmt(s);
    } else if (cmd.type === 'Subshell' || cmd.type === 'Group') {
      for (const s of cmd.body) visitStmt(s);
    } else if (cmd.type === 'FunctionDef') {
      visitCmd(cmd.body);
    }
  }
  function visitStmt(stmt: StatementNode) {
    for (const p of stmt.pipelines) for (const c of p.commands) visitCmd(c);
  }
  for (const stmt of script.statements) visitStmt(stmt);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Analyze a bash command string and determine if it ONLY targets identifiable
 * curl resources (plus safe read-only helpers).
 *
 * Uses the `just-bash` AST parser to walk every command in the script —
 * including nested command substitutions, process substitutions, subshells,
 * compound commands, etc.
 *
 * @returns An `AnalysisResult` indicating whether the command is fully
 *          analyzable and which curl resources it targets.
 */
export function analyzeBashCommand(command: string): AnalysisResult {
  let ast: ScriptNode;
  try {
    ast = parse(command);
  } catch {
    return {
      fullyAnalyzed: false,
      resources: [],
      warnings: ['Failed to parse command'],
    };
  }

  const ctx: WalkContext = {
    resources: [],
    warnings: [],
    fullyAnalyzed: true,
  };

  walkScript(ast, ctx);
  checkRedirections(ast, ctx);

  return {
    fullyAnalyzed: ctx.fullyAnalyzed,
    resources: ctx.resources,
    warnings: ctx.warnings,
  };
}

/**
 * Given a list of curl resources, return only the mutating ones (POST, PUT, PATCH, DELETE).
 */
export function getMutatingResources(resources: CurlResource[]): CurlResource[] {
  return resources.filter((r) => r.isMutating);
}

/**
 * Group resources by their resource type for display purposes.
 */
export function groupResourcesByType(resources: CurlResource[]): Map<string, CurlResource[]> {
  const groups = new Map<string, CurlResource[]>();
  for (const resource of resources) {
    const existing = groups.get(resource.resourceType) ?? [];
    existing.push(resource);
    groups.set(resource.resourceType, existing);
  }
  return groups;
}

/**
 * Returns `true` when the command contains at least one DELETE curl request.
 * DELETE requests should always require manual approval, even when
 * "Apply changes automatically" is enabled.
 */
export function containsDeleteRequest(analysis: AnalysisResult): boolean {
  return analysis.resources.some((r) => r.method === 'DELETE');
}
